import { useQuery } from "@tanstack/react-query";
import { ErrorBox, PageHeader, Spinner } from "../components/ui.js";

type UpdateKind = "new" | "improvement" | "fix" | "security";

interface UpdateItem {
  type: UpdateKind;
  text: string;
}

interface UpdateRelease {
  date: string;
  title: string;
  summary: string;
  items: UpdateItem[];
}

interface UpdateFeed {
  schemaVersion: number;
  version: string;
  revision: string;
  generatedAt: string;
  releases: UpdateRelease[];
}

const KIND_META: Record<UpdateKind, { label: string; className: string }> = {
  new: { label: "Novidade", className: "bg-brand-100 text-brand-800" },
  improvement: { label: "Melhoria", className: "bg-gold-100 text-gold-800" },
  fix: { label: "Correção", className: "bg-amber-100 text-amber-800" },
  security: { label: "Segurança", className: "bg-violet-100 text-violet-800" },
};

const dateFormat = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: "America/Sao_Paulo",
});

const dateTimeFormat = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "America/Sao_Paulo",
});

function formatReleaseDate(value: string): string {
  const parts = dateFormat.formatToParts(new Date(`${value}T12:00:00-03:00`));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? "";
  return `${part("day")} ${part("month")} ${part("year")}`;
}

async function loadUpdates(): Promise<UpdateFeed> {
  const response = await fetch("/updates.json", { cache: "no-store" });
  if (!response.ok) throw new Error("Não foi possível carregar o histórico de atualizações.");
  return response.json();
}

export function Atualizacoes() {
  const feed = useQuery({ queryKey: ["updates"], queryFn: loadUpdates, staleTime: 60_000 });

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Atualizações"
        subtitle="Linha do tempo das melhorias, correções e novidades publicadas no portal"
      />

      {feed.isLoading && <Spinner label="Carregando histórico…" />}
      {feed.error && <ErrorBox error={feed.error} />}

      {feed.data && (
        <>
          <section className="relative mb-10 overflow-hidden rounded-2xl bg-brand-800 px-6 py-7 text-white shadow-sm sm:px-8">
            <div className="absolute -right-12 -top-20 h-56 w-56 rounded-full border-[32px] border-white/5" />
            <div className="absolute -bottom-14 right-28 h-36 w-36 rotate-12 bg-gold-400/10" />
            <div className="relative flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-brand-50 ring-1 ring-white/15">
                  <span className="h-2 w-2 rounded-full bg-gold-400" />
                  Versão ativa
                </div>
                <div className="font-mono text-3xl font-bold tracking-tight">v{feed.data.version}</div>
                <p className="mt-2 max-w-xl text-sm leading-6 text-brand-100">
                  Este histórico é atualizado automaticamente a cada publicação em produção.
                </p>
              </div>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:text-right">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-brand-200">Revisão</dt>
                  <dd className="mt-1 font-mono font-semibold text-white">{feed.data.revision}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-brand-200">Gerado em</dt>
                  <dd className="mt-1 font-semibold text-white">{dateTimeFormat.format(new Date(feed.data.generatedAt))}</dd>
                </div>
              </dl>
            </div>
          </section>

          <ol className="relative space-y-8 before:absolute before:bottom-4 before:left-[19px] before:top-4 before:w-px before:bg-brand-200 before:content-[''] sm:before:left-[169px]">
            {feed.data.releases.map((release, index) => (
              <li key={`${release.date}-${release.title}`} className="relative grid gap-3 pl-12 sm:grid-cols-[145px_1fr] sm:gap-6 sm:pl-0">
                <div className="sm:pt-5 sm:text-right">
                  <time className="font-mono text-sm font-bold uppercase tracking-tight text-brand-800">
                    {formatReleaseDate(release.date)}
                  </time>
                  {index === 0 && (
                    <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.16em] text-brand-500">Mais recente</div>
                  )}
                </div>

                <span className="absolute left-3 top-4 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-brand-600 ring-4 ring-brand-50 sm:left-[162px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold-300" />
                </span>

                <article className="card overflow-hidden p-0 transition-shadow hover:shadow-md">
                  <div className="border-b border-brand-100 bg-brand-50/70 px-5 py-4 sm:px-6">
                    <h2 className="text-lg font-bold text-slate-900">{release.title}</h2>
                    <p className="mt-1 text-sm leading-6 text-slate-600">{release.summary}</p>
                  </div>
                  <ul className="divide-y divide-slate-100 px-5 sm:px-6">
                    {release.items.map((item, itemIndex) => {
                      const meta = KIND_META[item.type] ?? KIND_META.improvement;
                      return (
                        <li key={`${item.text}-${itemIndex}`} className="flex flex-col gap-2 py-4 sm:flex-row sm:items-start sm:gap-4">
                          <span className={`badge w-fit shrink-0 ${meta.className}`}>{meta.label}</span>
                          <span className="text-sm leading-6 text-slate-700">{item.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </article>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
